import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

export type MdaiTableRow = Record<string, ReactNode>;

export interface MdaiTableProps {
  id: string;
  tableTitle?: string | ReactNode;
  tableSubtitle?: string | ReactNode;
  headers: ReactNode[];
  rows: MdaiTableRow[];
}

export function MdaiTable({
  id,
  tableTitle,
  tableSubtitle,
  headers,
  rows,
}: MdaiTableProps) {
  return (
    <Stack
      sx={{
        justifySelf: "center",
        textAlign: "center",
        alignItems: "center",
        margin: "auto",
        maxWidth: { sm: "100%", md: 850 },
      }}
    >
      {typeof tableTitle === "string" ? (
        <Typography variant="h3" mb={2}>
          {tableTitle}
        </Typography>
      ) : (
        tableTitle
      )}

      {typeof tableSubtitle === "string" ? (
        <Typography mb={4} variant="body1">
          {tableSubtitle}
        </Typography>
      ) : (
        tableSubtitle
      )}

      <TableContainer
        sx={{
          backgroundColor: "transparent",
          width: "100%",
        }}
      >
        <Table
          sx={{
            justifySelf: "center",
            maxWidth: { sm: "100%", lg: 850 },
            tableLayout: "fixed",
            width: "100%",
          }}
          aria-label={`${id}-table`}
        >
          <TableHead>
            <TableRow
              sx={{
                verticalAlign: { sm: "bottom", lg: "bottom" },
              }}
            >
              {headers.map((header, idx) => (
                <TableCell
                  key={`${id}-th-${idx.toString()}`}
                  align="left"
                  sx={{ borderBottom: "1px solid var(--mdai-divider)" }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, rowIdx) => (
              <TableRow
                key={`${id}-tr-${rowIdx.toString()}`}
                sx={{
                  "&:last-child td, &:last-child td": { border: 0 },
                  verticalAlign: { sm: "top", lg: "top" },
                }}
              >
                {Object.entries(row).map(([key, value]) => (
                  <TableCell
                    key={`${id}-td-${rowIdx.toString()}-${key}`}
                    component="td"
                    scope="row"
                    sx={{
                      wordBreak: { sm: "break-all", lg: "normal" },
                      borderBottom: "1px solid var(--mdai-divider)",
                    }}
                  >
                    {value}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}
